import { useMutation, useQueryClient } from "react-query";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import CustomFormsDataService from "../../services/custom-forms-data-service/CustomFormsDataService";
import { RestError } from "../../services/rest-service/RestService";
import { IFormInstanceData } from "../../views/object-form-view/Models";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { keys } from "./Keys";

type MutationContextType = {
    previousInstances: IFormInstanceData[] | undefined;
};

export type UseSaveCustomFormInstanceParams = {
    customerID: number,
    //routeContext: RouteContextType,    
    groupID: number,
    objectID: number,
    objectType: eLynxObjectTypeEnum,
    startTime: Date | undefined,
    endTime: Date | undefined,
    offline: boolean
} & BaseUseMutationParameters<string, IFormInstanceData>;


export function useSaveCustomFormInstance({
    customerID,
    //routeContext,
    groupID,
    objectID,
    objectType,
    startTime,
    endTime,
    offline,
    onSuccess,
    onError
}: UseSaveCustomFormInstanceParams) {
    const queryClient = useQueryClient();

    return useMutation<string, RestError, IFormInstanceData, MutationContextType>(
        (formInstance) => {
            return CustomFormsDataService.saveInstance(
                customerID,
                groupID,
                formInstance,
                offline ?? false
            );
        },
        {
            onMutate: async (item: IFormInstanceData) => {

                await queryClient.cancelQueries(keys.all);

                const previousInstances = queryClient.getQueryData<IFormInstanceData[]>(
                    keys.list(
                        customerID,
                        groupID,
                        objectID,
                        objectType,
                        offline ?? false,
                        startTime,
                        endTime
                    )
                );

                queryClient.setQueryData<IFormInstanceData[]>(
                    keys.list(
                        customerID,
                        groupID,
                        objectID,
                        objectType,
                        offline ?? false,
                        startTime,
                        endTime
                    ),
                    (old?: IFormInstanceData[]) => {

                        const existing = old?.find(o => o.id === item.id);

                        return existing
                            ? old?.map(o => (o.id === item.id ? item : o)) ?? []
                            : [...(old ?? []), item];
                    }
                );

                return {
                    previousInstances: previousInstances ? [...previousInstances, item] : [item]
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _runTicket, context) => {

                if (error && context?.previousInstances) {
                    queryClient.setQueryData<IFormInstanceData[]>(
                        keys.list(
                            customerID,
                            groupID,
                            objectID,
                            objectType,
                            offline ?? false,
                            startTime,
                            endTime
                        ),
                        context?.previousInstances
                    );
                }

                queryClient.invalidateQueries(keys.lists());
            }
        }
    );
}
